import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiMessageCircle } from "react-icons/fi";
import navIcon from "../../assets/notificationIcon.png";
import { GlobalState } from "../../data/Context";
import { buildAssetUrl } from "../../data/authStorage";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import { createInitialsAvatar } from "../../utils/initialsAvatar";
import { CHAT_READ_EVENT, listChatGroups } from "../../api/chat";
import { listMyNotifications, NOTIFICATION_INBOX_CHANGED_EVENT } from "../../api/notifications";

const Navbar = () => {
  const navigate = useNavigate();
  const { organization } = useContext(GlobalState);
  const { companyAccount, currentEmployeeName, currentEmployeePhoto } = useCurrentEmployee();
  const [unreadChats, setUnreadChats] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const organizationName = organization?.name || "";
  const profileImage = companyAccount
    ? buildAssetUrl(organization?.logoUrl || organization?.logo || organization?.image) || createInitialsAvatar(organizationName)
    : buildAssetUrl(currentEmployeePhoto) || currentEmployeePhoto || createInitialsAvatar(currentEmployeeName);

  function handleNotification() {
    navigate("/notification");
  }

  function handleProfile() {
    navigate("/profile");
  }

  const refreshUnreadChats = useCallback(async () => {
    try {
      const response = await listChatGroups();
      setUnreadChats(Number(response?.unreadCount || 0));
    } catch {
      setUnreadChats(0);
    }
  }, []);

  const refreshUnreadNotifications = useCallback(async () => {
    if (companyAccount) {
      setUnreadNotifications(0);
      return;
    }
    try {
      const response = await listMyNotifications({ page: 1, limit: 1 });
      setUnreadNotifications(Number(response?.unreadCount || 0));
    } catch {
      setUnreadNotifications(0);
    }
  }, [companyAccount]);

  useEffect(() => {
    const refreshHeaderCounts = () => {
      if (document.visibilityState === "visible") Promise.all([refreshUnreadChats(), refreshUnreadNotifications()]);
    };
    refreshHeaderCounts();
    const timer = window.setInterval(refreshHeaderCounts, 30000);
    window.addEventListener("focus", refreshHeaderCounts);
    window.addEventListener(CHAT_READ_EVENT, refreshHeaderCounts);
    window.addEventListener(NOTIFICATION_INBOX_CHANGED_EVENT, refreshHeaderCounts);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshHeaderCounts);
      window.removeEventListener(CHAT_READ_EVENT, refreshHeaderCounts);
      window.removeEventListener(NOTIFICATION_INBOX_CHANGED_EVENT, refreshHeaderCounts);
    };
  }, [refreshUnreadChats, refreshUnreadNotifications]);

  return (
    <>
      <div
        className="container flex items-center justify-between px-4 sm:px-6"
      >
        <h1 className=" font-Nunito text-xl font-[500]">
          {organizationName ? `Hi, ${organizationName}!` : "Hi!"}
        </h1>

        <div className="flex items-center gap-4">
          <button
            type="button"
            className="relative inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#071C50] shadow-sm ring-1 ring-slate-100 hover:bg-slate-100"
            onClick={() => navigate("/chat")}
            aria-label={unreadChats ? `Chats, ${unreadChats} unread messages` : "Chats"}
            title={unreadChats ? `${unreadChats} unread message${unreadChats === 1 ? "" : "s"}` : "Chats"}
          >
            <FiMessageCircle className="h-5 w-5" />
            {unreadChats > 0 ? (
              <span className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white ring-2 ring-white">
                {unreadChats > 99 ? "99+" : unreadChats}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            className="relative p-2 rounded-full bg-white hover:bg-slate-100"
            onClick={handleNotification}
            aria-label={unreadNotifications ? `Notifications, ${unreadNotifications} unread` : "Notifications"}
          >
            <img
              className="w-8 h-8"
              src={navIcon}
              alt="Notifications"
            />
            {unreadNotifications > 0 ? <span className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white ring-2 ring-white">{unreadNotifications > 99 ? "99+" : unreadNotifications}</span> : null}
          </button>

          <button
            type="button"
            className="p-2 rounded-full bg-white hover:bg-slate-100"
            onClick={handleProfile}
          >
            <img
              className="h-14 w-14 rounded-full object-cover"
              src={profileImage}
              alt="Profile"
            />
          </button>
        </div>
      </div>

      <hr className=" border my-2 bg-[#CCCCCC] " />
    </>
  );
};

export default Navbar;
